import { replaceRbacAdminRoleAssignmentsOptions, useAdmin } from '@gozoki/api'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import z from 'zod/v3'
import { ACCESS_ADMIN_EDIT } from '~/components/routes/administration/access/permissions'
import { AdminApiKeysSection } from '~/components/routes/admins/AdminApiKeysSection'
import { AdminMfaSection } from '~/components/routes/admins/AdminMfaSection'
import {
  CreateOrUpdateAdminForm,
  type AdminAccountForm,
} from '~/components/routes/admins/CreateOrUpdateAdminForm'
import type { Admin } from '@gozoki/api-types'
import { Spinner } from '~/components/ui/spinner'
import { api } from '~/lib/tuyau'
import { getApiErrorMessage } from '~/lib/utils'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/administration/admins/$id/edit')({
  staticData: {
    permission: ['read', 'users.bo-users.user'],
  },
  params: {
    parse: z.object({ id: z.coerce.number() }).parse,
  },
  component: EditAdminPage,
})
withRoutePermissions(Route)

const toFormValues = (admin: Admin): AdminAccountForm => ({
  firstName: admin.firstName,
  lastName: admin.lastName,
  email: admin.email,
  password: null,
})

function EditAdminPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()

  const { data: admin, isLoading } = useAdmin(id)
  const assignRolesMutation = useMutation(replaceRbacAdminRoleAssignmentsOptions())
  const updateAdminMutation = useMutation(api.users.adminUpdate.mutationOptions())

  const canEdit = usePermissionGuard(ACCESS_ADMIN_EDIT).authorized

  if (isLoading || !admin) {
    return (
      <div className="flex justify-center p-16">
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <CreateOrUpdateAdminForm
        title={canEdit ? 'Modifier un administrateur' : 'Fiche administrateur'}
        submitLabel="Modifier"
        submitPermission={['edit', 'users.bo-users.user']}
        hidePasswordByDefault
        adminId={id}
        defaultValues={toFormValues(admin)}
        onCancel={() => navigate({ to: '/administration/access' })}
        onSubmit={async (values, roleAssignments) => {
          try {
            await updateAdminMutation.mutateAsync({ params: { id }, body: values })
            // Les affectations s'écrivent après la fiche : un échec ici laisse la fiche à jour et
            // le message d'erreur dit précisément ce qui n'a pas été enregistré.
            if (roleAssignments) {
              await assignRolesMutation.mutateAsync({
                adminId: id,
                data: { assignments: roleAssignments },
              })
            }
            navigate({ to: '/administration/access' })
          } catch (error) {
            // Fiche (Tuyau) puis rôles (client axios) : le message lit l'un comme l'autre.
            toast.error(getApiErrorMessage(error, "Le compte n'a pas pu être modifié."))
          }
        }}
      />
      <AdminMfaSection adminId={id} />
      <AdminApiKeysSection adminId={id} />
    </>
  )
}
